import { SortColumn } from "react-data-grid";

import { ColorConstants, ProjectIssuanceConstants } from "../../../../../../../../constants";
import { ResultType } from "../../../../../../../../models";
import { GetProjectIssuancesRequest, GetProjectIssuancesResponse } from "../../../../../../../../service/publicquery";
import {
  getHabitatFromStandardSpecificIssuanceData,
  getInventoryChartShape,
  getInventoryDefaultSort,
  getInventoryVerifiedColours,
  getInventoryVerifiedPredicate,
  getInventoryXAxisLabel,
  getNutrientChartColours,
} from "../../../../../../../../utils";
import { PreAccessInventoryChartData } from "../models/preAccessInventoryTab.models";

const SURFACE = "assetManagerPreAccess";

export const getPreAccessDefaultSortingCriteria = (unitMeasurement: string): SortColumn[] => {
  return [
    {
      columnKey: getInventoryDefaultSort(SURFACE, unitMeasurement).gridColumnKey,
      direction: "ASC",
    },
  ];
};

export const getPreAccessDefaultSortKey = (
  unitMeasurement: string
): NonNullable<GetProjectIssuancesRequest["sort"]>[number]["key"] =>
  getInventoryDefaultSort(SURFACE, unitMeasurement).serverSortKey as NonNullable<
    GetProjectIssuancesRequest["sort"]
  >[number]["key"];

export const buildPreAccessChartData = (
  issuances: GetProjectIssuancesResponse["results"],
  unitMeasurement: string,
  showAvailable: boolean
): PreAccessInventoryChartData => {
  const shape = getInventoryChartShape(SURFACE, unitMeasurement);
  const isVerifiedIssuance = getInventoryVerifiedPredicate(shape.verifiedBy);

  const xAxisLabels: (string | string[])[] = [];
  const verifiedTotal: number[] = [];
  const verifiedAvailable: number[] = [];
  const pendingTotal: number[] = [];
  const pendingAvailable: number[] = [];

  /*
    The two grouped shapes. Both accumulate many issuances into one bar per key and leave the pending
    series at zero, because neither measurement has a verified/pending split - Biodiversity has never
    had one, and Nutrient Neutrality has none either (shape.verifiedBy is "none" for both).

    They differ only in the key and how it renders: a habitat key is a two-part label drawn on two
    lines, a nutrient is a single word. Nutrient Neutrality has no vintage at all, so without this
    branch it fell to the ungrouped one below and every bar was labelled "undefined - undefined".
  */
  if (shape.groupBy === "habitat" || shape.groupBy === "quantityType") {
    const isHabitat = shape.groupBy === "habitat";
    const groupedResults: Record<string, { unitQuantityTotal: number; unitQuantityAvailable: number }> = {};

    issuances.forEach((issuance) => {
      const groupKey = isHabitat
        ? getHabitatFromStandardSpecificIssuanceData(issuance.standardSpecificIssuanceData).join("-")
        : (issuance.quantityType ?? "");

      if (!groupedResults[groupKey]) {
        groupedResults[groupKey] = {
          unitQuantityTotal: 0,
          unitQuantityAvailable: 0,
        };
      }

      groupedResults[groupKey].unitQuantityTotal += issuance.unitQuantityTotal;
      groupedResults[groupKey].unitQuantityAvailable += issuance.unitQuantityAvailable;
    });

    Object.keys(groupedResults).forEach((groupKey) => {
      xAxisLabels.push(isHabitat ? groupKey.split("-") : groupKey);
      verifiedTotal.push(groupedResults[groupKey].unitQuantityTotal);
      if (showAvailable) verifiedAvailable.push(groupedResults[groupKey].unitQuantityAvailable);
      pendingTotal.push(0);
      pendingAvailable.push(0);
    });
  } else {
    issuances.forEach((issuance) => {
      const verified = isVerifiedIssuance(issuance);

      xAxisLabels.push(getInventoryXAxisLabel(SURFACE, issuance));
      verifiedTotal.push(verified ? issuance.unitQuantityTotal : 0);
      pendingTotal.push(verified ? 0 : issuance.unitQuantityTotal);
      if (showAvailable) {
        verifiedAvailable.push(verified ? issuance.unitQuantityAvailable : 0);
        pendingAvailable.push(verified ? 0 : issuance.unitQuantityAvailable);
      }
    });
  }

  const pendingAllocated = pendingTotal.map((total, index) =>
    showAvailable && total === pendingAvailable[index] ? 0 : total - (showAvailable ? pendingAvailable[index] : 0)
  );
  const verifiedAllocated = verifiedTotal.map((total, index) =>
    showAvailable && total === verifiedAvailable[index] ? 0 : total - (showAvailable ? verifiedAvailable[index] : 0)
  );

  /*
    Nutrient Neutrality colours its bars by nutrient rather than by verified/pending, as the project
    and public charts do: there is no pending concept to colour by, so the only thing left to
    distinguish is which nutrient a bar belongs to. A series carries an array of colours indexed by
    category, which is how Chart.js varies one series' colour per bar, and the palettes come from
    getNutrientChartColours so the bars and the chart key cannot drift apart.

    Two series rather than four - the pending pair was permanently zero for this measurement.

    Gated on the measurement as well as the shape: Restoration shares the shape but has no nutrients,
    so it falls through to the four-series return in its own colour family, with the pending pair zero.
  */
  if (
    shape.groupBy === "quantityType" &&
    unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY
  ) {
    const nutrients = xAxisLabels as string[];
    const palettes = nutrients.map((nutrient, index) => getNutrientChartColours(nutrient, index));

    return {
      xAxisLabels,
      chartKeyNutrients: nutrients,
      stackedChartData: [
        {
          label: "Allocated",
          data: verifiedAllocated,
          backgroundColor: palettes.map((palette) => palette.dark),
          hoverBackgroundColor: palettes.map((palette) => palette.darkHover),
        },
        {
          label: "Available",
          data: verifiedAvailable,
          backgroundColor: palettes.map((palette) => palette.light),
          hoverBackgroundColor: palettes.map((palette) => palette.lightHover),
        },
      ],
    };
  }

  const verifiedColours = getInventoryVerifiedColours(unitMeasurement);

  return {
    xAxisLabels,
    chartKeyNutrients: [],
    stackedChartData: [
      {
        label: "Allocated",
        data: pendingAllocated,
        backgroundColor: ColorConstants.INDIGO_500,
        hoverBackgroundColor: ColorConstants.INDIGO_400,
      },
      {
        label: "Available",
        data: pendingAvailable,
        backgroundColor: ColorConstants.INDIGO_100,
        hoverBackgroundColor: ColorConstants.BLUE_100,
      },
      {
        label: "Allocated",
        data: verifiedAllocated,
        backgroundColor: verifiedColours.dark,
        hoverBackgroundColor: verifiedColours.darkHover,
      },
      {
        label: "Available",
        data: verifiedAvailable,
        backgroundColor: verifiedColours.light,
        hoverBackgroundColor: verifiedColours.lightHover,
      },
    ],
  };
};

export const buildEmptyResultType = (pageSize: number): ResultType => ({
  resultData: [],
  paging: {
    pageSize,
    totalCount: 0,
    startCursor: "",
    endCursor: "",
    hasNextPage: false,
    hasPreviousPage: false,
  },
});
